import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import usePageTitle from '../hooks/usePageTitle';

const TermsOfService: React.FC = () => {
  usePageTitle('Terms of Service');

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Button variant="ghost" className="mb-6" asChild>
          <Link to="/my-apps">
            <ChevronLeft aria-hidden="true" />
            Back to Portal
          </Link>
        </Button>

        <h1 className="text-2xl font-semibold mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-8">Effective Date: April 1, 2026</p>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-base font-semibold mb-2">1. Acceptance of Terms</h2>
            <p>By accessing or using the Dory Portal ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service. These Terms apply to all users, including organization administrators and members.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">2. About the Service</h2>
            <p>The Dory Portal is a research platform developed and operated by the <strong>Institute for Sensing and Embedded Network Systems Engineering (I-SENSE)</strong> at <strong>Florida Atlantic University (FAU)</strong>. It enables authorized users to manage applications, sensors, running processors, processor templates, and organization resources connected to the Dory platform. Access is granted through organization membership and role-based permissions.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">3. Eligibility and Account Responsibilities</h2>
            <p className="mb-2">Use of the Service is limited to authorized individuals affiliated with I-SENSE, FAU, or partner organizations granted access by I-SENSE. By using the Service, you represent that you are authorized to do so.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You must notify us immediately of any unauthorized use of your account.</li>
              <li>You are responsible for all activities that occur under your account.</li>
              <li>You may not share your credentials or allow others to access the Service using your account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">4. Permitted Use</h2>
            <p className="mb-2">You agree to use the Service only for lawful, research-related purposes consistent with FAU's policies and in accordance with these Terms. You may not:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the Service in any way that violates applicable laws, regulations, or FAU's Acceptable Use Policy.</li>
              <li>Attempt to gain unauthorized access to any part of the Service or its related systems.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service.</li>
              <li>Use the Service to transmit malicious code or engage in any activity that could harm the platform or other users.</li>
              <li>Use the Service for commercial purposes outside of authorized research activities.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">5. Organization and Research Data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Data you submit through the Service belongs to your organization or research group.</li>
              <li>You are responsible for ensuring that your use of the Service and any data submitted complies with applicable laws, IRB protocols, and data governance requirements.</li>
              <li>We process your data as described in our <Link to="/legal/privacy-policy" className="text-primary underline underline-offset-4">Privacy Policy</Link>.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">6. Intellectual Property</h2>
            <p>The Dory platform, including its software, designs, and documentation, is the intellectual property of Florida Atlantic University and the I-SENSE Institute. Nothing in these Terms grants you any right to use FAU's or I-SENSE's trademarks, logos, or other proprietary information beyond what is necessary for authorized use of the Service.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">7. Modifications to the Service</h2>
            <p>We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">8. Disclaimer of Warranties</h2>
            <p className="uppercase text-xs tracking-wide">The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. As a research platform operated by a university, the Service is provided primarily for academic and research purposes.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">9. Limitation of Liability</h2>
            <p className="uppercase text-xs tracking-wide">To the maximum extent permitted by applicable law, Florida Atlantic University and I-SENSE shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">10. Governing Law</h2>
            <p>These Terms are governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes shall be subject to the jurisdiction of the courts of Palm Beach County, Florida.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">11. Changes to These Terms</h2>
            <p>We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms within the Service. Your continued use of the Service after such changes constitutes your acceptance of the new Terms.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">12. Contact</h2>
            <p>If you have questions about these Terms, please contact the I-SENSE Institute:</p>
            <address className="not-italic mt-2 text-muted-foreground">
              Institute for Sensing and Embedded Network Systems Engineering (I-SENSE)<br />
              Florida Atlantic University<br />
              777 Glades Road<br />
              Boca Raton, FL 33431<br />
              <a href="mailto:i-sense@fau.edu" className="text-primary underline underline-offset-4">i-sense@fau.edu</a>
            </address>
          </section>
        </div>

        <div className="mt-10 border-t pt-6 text-xs text-muted-foreground">
          <Link to="/legal/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
